import { Navigate, useLocation, useNavigate } from "@solidjs/router";
import { hasSignedInCookie } from "@storytell/auth-ssr-implementation/src/redirectHintCookies";
import { onMount, Show } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { HomePage } from "~/components/marketing/home";
import { hasFirebaseSession } from "~/lib/firebaseSessionProbe";

const isSignedInFromCookie = (): boolean => {
  if (isServer) return hasSignedInCookie(getRequestEvent()?.request.headers.get("cookie") ?? "");
  return hasSignedInCookie(document.cookie);
};

// Hash is never sent to the server, so it's empty on SSR — but browsers preserve the
// original hash across a 302, so /#foo → /app#foo automatically. Search params we have
// to forward explicitly.
const buildAppTarget = (search: string, hash: string): string => `/app${search}${hash}`;

// The landing page before the redesign took the role as /?for=…, and those links
// are still shared. Each role has its own page now.
const ROLE_PAGES: Record<string, string | undefined> = {
  builders: "/for-builders",
  executives: "/for-executives",
};

export default function MarketingHomeRoute() {
  const location = useLocation<{ skipAutoRedirect?: boolean }>();
  const rolePage = ROLE_PAGES[String(location.query.for)];
  if (rolePage) {
    const params = new URLSearchParams(location.search);
    params.delete("for");
    const rest = params.toString();
    return <Navigate href={`${rolePage}${rest ? `?${rest}` : ""}${location.hash}`} />;
  }

  // /?landing, /?video and /?s pin the marketing page for everyone, signed-in or not, so
  // the URL can be shared publicly without bouncing visitors into /app.
  const forceLanding = location.query.landing != null || location.query.video != null || location.query.s != null;
  const skipAutoRedirect = forceLanding || location.state?.skipAutoRedirect === true;
  const signedIn = !skipAutoRedirect && isSignedInFromCookie();
  // Deep links like /?chat=hello or /?prompt=… are app payloads, not landing
  // traffic. Chat/prompt handling lives under /app, so forward straight there
  // with the query intact regardless of auth state — the app handles guest
  // vs signed-in.
  const hasAppDeepLink = location.query.chat != null || location.query.prompt != null;
  const redirectToApp = !forceLanding && (signedIn || hasAppDeepLink);

  return (
    <Show when={!redirectToApp} fallback={<Navigate href={buildAppTarget(location.search, location.hash)} />}>
      <ClientFallbackRedirect skip={skipAutoRedirect} />
      <HomePage />
    </Show>
  );
}

// Fallback for users with an active Firebase session but no isSignedIn cookie yet
// (e.g. pre-existing sessions on first visit after deploy). Probes IndexedDB on mount.
const ClientFallbackRedirect = (props: { skip: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectIfSignedIn = async () => {
    if (await hasFirebaseSession()) navigate(buildAppTarget(location.search, location.hash), { replace: true });
  };
  onMount(() => {
    if (props.skip) return;
    void redirectIfSignedIn();
  });
  return null;
};
